import React, { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../../molecules/DashboardLayout";
import { Row, Col, Pagination, Card, Select, Typography } from "antd";
import { SectionHeaderV2 } from "../../organism/SectionHeaderV2";
import { useDispatch, useSelector } from "react-redux";
import { getMoveOuts, setMoveoutFilters } from "../Contracts/ducks/actions";
import dayjs from "dayjs";
import { determineTerminationType } from "../../../utils/dateHelper";
import { useSearchHistory } from "../../../utils/filterHelper";
import { setFilterRenewal } from "../App/SearchFilters/ducks/actions";
import LoaderTsx from "../Loader.tsx";
import { useNavigate } from "react-router-dom";
import { formatString } from "../../../utils/formatString";

import { MoveOutList } from "./MoveOutList";
import { useMoveOutsSearch } from "./hooks";
import { searchByOptions as baseSearchByOptions } from "./constants/searchByOptions";
import { useFeatureToggleSearchByOptions } from "../../../hooks/search";

import { moveOutCol, moveOutColumn } from "./moveOutCols";
import DragAndDropList from "../../atoms/Grid/gridColumnSort";
import { getGridSettings } from "../../../utils/globalService";
import { getOrgByID } from "../../modules/Maintenance/Tickets/ducks/actions";
import { useGridReset } from "../../../hooks/useGridReset";
import { listFilterConfigWithPortfolioUnits } from "../../../utils/helper";
import { BookingFiltersState, FilterOption, useListingFilters } from "../../../hooks/useListingFilters";
import { getPropertiesDrop } from "../Properties/ducks/actions";
import { emptyPropertyUnits, getBookingUnitList, getPropertyUnits } from "../Properties/PropertyDetail/ducks/actions";

const gridProps = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 220,
};

const moveOutDropdown = [{ id: 1, name: "Edit Move-out", status: "" }];

const searchKeys = [
  { value: "name", label: "Name" },
  { value: "unitCode", label: "Unit Code", default: true },
  { value: "property", label: "Property" },
  { value: "propertyAdmin", label: "Property Admin" },
];

const forMoveOutFlow = [
  { label: "All", value: null },
  { label: "Awaiting Payment", value: "awaiting_payment" },
  { label: "Awaiting Refund", value: "awaiting_refund" },
  { label: "Move-out Confirmed", value: "moveout_confirmed" },
  { label: "Settlement Approved", value: "settlement_approved" },
  { label: "Settlement Confirmed", value: "settlement_confirmed" },
  { label: "Settled", value: "settled" },
];

const MoveOut = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [searchKey, setSearchKeys] = useState(searchKeys);
  const [isLoading, setIsLoading] = useState(false);
  const [exportData, setExportData] = useState(false);
  const moveoutFilters: any = useSelector((state: any) => state?.contracts?.moveoutFilters);
  const { searchHistory } = useSearchHistory(setFilterRenewal);
  const [selectedSearchBy, setSelectedSearchBy] = useState(moveoutFilters?.searchBy || "unitCode");
  const [searchValue, setSearchValue] = useState(moveoutFilters?.search ?? "");

  const contractApi = useSelector((state: any) => state?.contracts?.contractList);
  const [moveOutColumns, setMoveOutColumns] = useState(() => [...moveOutCol()]);
  const [detectColumnChanges, setDetectColumnChanges] = useState(false);
  const organization = useSelector((state: any) => state.tickets.orgData);
  const { resetGrid, setGridReset } = useGridReset();
  const [unitLoading, setUnitLoading] = useState(false);

  useEffect(() => {
    dispatch(getPropertiesDrop());
    dispatch(getPropertyUnits(null, null, false, setUnitLoading));
    return () => {
      dispatch(emptyPropertyUnits());
    };
  }, []);

  const { searchByOptions } = useFeatureToggleSearchByOptions(baseSearchByOptions);
  const { selectOptions, handleSearch, handleChangeSearchBy } = useMoveOutsSearch({
    selectedSearchBy,
    setSelectedSearchBy,
    setSearchValue,
  });

  const propertyList = useSelector((state: any) => state.dashboard.propertiesDrop || []);
  const bookingUnits = useSelector((state: any) => state.propertyDetails.bookingUnitsDropdown || []);
  const portfolioUnits = useSelector((state: any) => state.propertyDetails.unitsDropdown || []);
  const propertyOptions: FilterOption[] = propertyList;

  const listingFilterConfig = useCallback(
    (state: BookingFiltersState) =>
      listFilterConfigWithPortfolioUnits({
        state,
        propertyOptions,
        portfolioUnits,
        bookingUnits,
        portfolioUnitsLoading: unitLoading,
      }),
    [propertyOptions, portfolioUnits, bookingUnits, unitLoading],
  );

  const { viewOption: listingFilterOptions, handleFilterChange: hookFilterChange, updateValue, filtersState } =
    useListingFilters<BookingFiltersState>({
      initialState: {
        usagesType: { value: "all", label: "All" },
        property: { value: "all", label: "All" },
        unit: { value: "all", label: "All" },
      },
      configBuilder: listingFilterConfig,
      handlers: {
        property: ({ option, setState, setUpdateValue }) => {
          setState((prev) => ({
            ...prev,
            property: option,
            unit: null,
          }));
          setUpdateValue({ label: "unit", value: null });
          const pid = option?.value;
          if (pid && pid !== "all" && pid !== "") {
             dispatch(getPropertyUnits(pid, null, false, setUnitLoading));
          } else {
            dispatch(getPropertyUnits(null, null, false, setUnitLoading));
          }
        },
      },
      sections: { usagesType: false },
    });

  const listingFilterParams = () => ({
    usagesType:
      filtersState.usagesType?.value === "all" || !filtersState.usagesType?.value
        ? ""
        : String(filtersState.usagesType.value),
    propertyId:
      filtersState.property?.value === "all" ||
      filtersState.property?.value === "" ||
      filtersState.property?.value == null
        ? ""
        : filtersState.property.value,
    unitId:
      !filtersState.unit?.value || filtersState.unit?.value === "all" || filtersState.unit?.value === ""
        ? ""
        : filtersState.unit.value,
  });

  const optionsForStatus = [...forMoveOutFlow];

  const [filterOptionsForMoveOut] = useState(() => {
    const saved = moveoutFilters?.filterStatus;
    const initialStatus =
      saved === undefined || saved === null
        ? forMoveOutFlow[0]
        : forMoveOutFlow.find((o) => o.value === saved) ?? forMoveOutFlow[0];
    return [
      {
        name: "status",
        label: "Status",
        initialValue: initialStatus,
        title: "1",
        options: optionsForStatus,
      },
    ];
  });

  const [filterStatus, setStatusFilter] = useState(moveoutFilters?.filterStatus ?? null);

  const MoreAction = (row) => {
    const { Text } = Typography;

    const onChangeHanldler = async () => {
      const leaseId = row?.data?.leaseId;
      navigate(`/move-out/summery/${leaseId}`, {
        state: { moveOut: true },
      });
    };

    return (
      <Select
        onChange={onChangeHanldler}
        className="Sentence"
        placeholder="Select an action"
        style={{ width: 150 }}
        allowClear
      >
        <>
          {moveOutDropdown.map((value, key) => (
            <React.Fragment key={key}>
              <Select.Option value={value.name}>
                <Text style={{ color: `${value.status}` }}>{value.name}</Text>
              </Select.Option>
            </React.Fragment>
          ))}
        </>
      </Select>
    );
  };

  const setRenewalStatus = (data) => {
    return formatString(data?.moveOutStatus);
  };

  const handlePagination = (page, pageSize) => {
    setCurrentPage(page);
    setLimit(pageSize);
    const params: Record<string, unknown> = {};
    if (filterStatus !== null && filterStatus !== undefined && filterStatus !== "") {
      params["renewalStatus"] = filterStatus;
    }
    const listing = listingFilterParams();
    if (listing.usagesType) params["usagesType"] = listing.usagesType;
    if (listing.propertyId) params["propertyId"] = listing.propertyId;
    if (listing.unitId) params["unitId"] = listing.unitId;
    setIsLoading(true);
    dispatch(getMoveOuts(pageSize, page, searchValue, selectedSearchBy, params, null, setIsLoading));
  };

  useEffect(() => {
    const buildLeaseColumnsWithKyc = (org) => {
      const kycFields = [...(org?.commercialKycForm || []), ...(org?.residentialKycForm || [])].filter(
        (field, index, self) => index === self.findIndex((f) => f.name === field.name),
      );

      const dynamicKycCols = kycFields.map((field, index) => ({
        id: 50 + index,
        headerName: field.label,
        field: `kycDetails.${field.name}`,
        checked: false,
        hide: true,
      }));

      const mergedLeaseCols = [...moveOutCol(), ...dynamicKycCols];
      getGridConfig(mergedLeaseCols);
      setMoveOutColumns(mergedLeaseCols);
    };

    const init = async () => {
      const isOrgEmpty = !organization || (typeof organization === "object" && Object.keys(organization).length === 0);

      if (isOrgEmpty) {
        const orgData = localStorage?.getItem("login-response");
        const orgId = orgData ? JSON.parse(orgData) : null;
        if (orgId) {
          await dispatch(getOrgByID(orgId?.orgId));
        }
      } else {
        buildLeaseColumnsWithKyc(organization);
      }
    };

    init();
  }, [organization, resetGrid]);

  const getGridConfig = async (columns) => {
    let result = await getGridSettings("moveOutContracts", columns, resetGrid);
    if (result?.length > 0) {
      setMoveOutColumns(result);
      setGridReset(false);
    }
  };

  useEffect(() => {
    if (moveoutFilters?.searchBy) {
      const setArray = searchKey.map((item) => {
        let val = null;
        if (item.value === moveoutFilters?.searchBy) {
          val = { ...item, default: true };
        } else {
          val = { ...item, default: false };
        }
        return val;
      });
      setSearchKeys(setArray);
    }
  }, [moveoutFilters, moveoutFilters?.searchBy]);

  useEffect(() => {
    if (
      moveoutFilters?.search != searchValue ||
      moveoutFilters?.searchBy != selectedSearchBy ||
      moveoutFilters?.filterStatus != filterStatus
    )
      dispatch(
        setMoveoutFilters({ search: searchValue, searchBy: selectedSearchBy, filterStatus, currentPage }),
      );
    const params: Record<string, unknown> = {};

    if (filterStatus !== null && filterStatus !== undefined && filterStatus !== "") {
      params["renewalStatus"] = filterStatus;
    }
    const listing = listingFilterParams();
    if (listing.usagesType) params["usagesType"] = listing.usagesType;
    if (listing.propertyId) params["propertyId"] = listing.propertyId;
    if (listing.unitId) params["unitId"] = listing.unitId;

    if (exportData) {
      let checkedColumns = moveOutColumns
        .filter((col) => col.checked && col.headerName !== "Actions")
        .map((col) => col.headerName)
        .join(", ");
      params["columns"] = checkedColumns;
      params["exportData"] = exportData;
      params["totalCount"] = contractApi?.totalCount;
    }

    setCurrentPage(1);
    setIsLoading(true);
    dispatch(getMoveOuts(limit, 1, searchValue, selectedSearchBy, params, setExportData, setIsLoading));
  }, [
    searchValue,
    selectedSearchBy,
    filterStatus,
    exportData,
    filtersState.usagesType?.value,
    filtersState.property?.value,
    filtersState.unit?.value,
  ]);

  useEffect(() => {
    let key = Object.keys(searchHistory)[0];
    if (searchKey.length > 0)
      searchKey.map((item) => {
        if (key == item.value) item["default"] = true;
        else item["default"] = false;
        return item;
      });
  }, [searchHistory]);

  const filterData = (data = []) => {
    const dataCopy = [...data];

    if (dataCopy.length > 0) {
      for (let key in dataCopy) {
        if (dataCopy[key].leaseType === "new" && dataCopy[key].leaseStatus === "active") {
          const leaseTerms = {
            ...dataCopy[key].leaseTerms,
            ...{
              leaseStartDate: dayjs(dataCopy[key].leaseTerms.leaseEndDate).add(1, "day").toISOString(),
              leaseEndDate: dayjs(dataCopy[key].leaseTerms.leaseEndDate).add(1, "year").toISOString(),
            },
          };

          dataCopy[key]["leaseTerms"] = {
            ...dataCopy[key]["leaseTerms"],
            ...leaseTerms,
          };
        }
      }
    }

    return dataCopy;
  };

  const handleViewFilter = (selected, name) => {
    if (name === "usagesType" || name === "property" || name === "unit") {
      hookFilterChange(selected, name);
      return;
    }
    if (name === "status") {
      setStatusFilter(selected?.value);
    }
  };

  //  for export data and send in email

  const exportAction = () => {
    setExportData(true);
  };

  return (
    <DashboardLayout>
      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <SectionHeaderV2
                noBorder={true}
                heading="Move-outs"
                propertyAdminOptions={true}
                total={contractApi?.totalCount ?? 0}
                onSearch={handleSearch}
                onChangeSearchBy={handleChangeSearchBy}
                defaultSearch={searchValue}
                searchByOptions={searchByOptions}
                selectOptions={selectOptions}
                viewOption={[...listingFilterOptions, ...filterOptionsForMoveOut]}
                viewOptionLabel={""}
                justifiedItems={true}
                justifyEnd={true}
                downloadAction={exportAction}
                handleView={handleViewFilter}
                updateValue={updateValue}
                filterStyle={{ marginRight: "10px" }}
              />
            </Col>
            <Col span={23}>
              <Pagination
                className="ag-property-text-pagination"
                total={contractApi?.totalCount ?? 0}
                showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} Renewals`}
                pageSize={contractApi?.perPage ?? 50}
                defaultCurrent={1}
                current={currentPage || moveoutFilters?.currentPage}
                onChange={handlePagination}
              />
            </Col>
            <Col span={1}>
              <div style={{ display: "flex", justifyContent: "end" }}>
                <DragAndDropList
                  columns={moveOutColumns}
                  setModifiedColumns={setMoveOutColumns}
                  changePicker={setDetectColumnChanges}
                  detectColumnChanges={detectColumnChanges}
                  screenName="moveOutContracts"
                  setGridReset={setGridReset}
                  filterStatus={filterStatus}
                />
              </div>
            </Col>
            <Col span={24}>
              <Row gutter={[24, 24]} justify="end">
                <Col span={24}>
                  <Card bordered={false} className="ag-nospace-body">
                    {isLoading ? (
                      <LoaderTsx />
                    ) : (
                      <MoveOutList
                        columns={moveOutColumn(
                          moveOutColumns,
                          MoreAction,
                          setRenewalStatus,
                          determineTerminationType,
                        )}
                        gridProps={gridProps}
                        data={filterData(contractApi?.data) ?? []}
                      />
                    )}
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </DashboardLayout>
  );
};

export default MoveOut;
